/**
 * Fax Agent Dashboard Component
 * 
 * Main dashboard for managing fax communications with tabs, filtering, and table display.
 * 
 * @component
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import CustomContainer from "../common/CustomContainer";
import {
    Button,
    CustomDialog,
    useMenu
} from "@voicecare.ai/ui-library";
import { useTheme } from "../../ThemeContext";
import {
    Send as SendIcon,
    Add as AddIcon
} from "@mui/icons-material";
import StickyFlexTable from "../common/StickyFlexTable";
import { Search } from "@voicecare.ai/ui-library";
import { useSmartOSearch } from "oreacto";
import EnhancedSendFaxPanel from "./EnhancedSendFaxPanel";
import FaxAgentService from "../../services/fax-agent-service";
import PurchasedNumbers from "./PurchasedNumbers";

/**
 * Format fax number to +1 (XXX) XXX-XXXX format
 * @param {string} number - Raw fax number
 * @returns {string} Formatted fax number
 */
const formatFaxNumber = (number) => {
    if (!number) return 'N/A';
    
    // Remove all non-digits
    const digits = number.replace(/\D/g, '');
    
    // Handle different lengths
    if (digits.length === 11 && digits[0] === '1') {
        // Format as +1 (XXX) XXX-XXXX
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    } else if (digits.length === 10) {
        // Assume US number without country code
        return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    
    // Return original if format doesn't match
    return number;
};

/**
 * Convert string to Title Case (first letter caps, rest lowercase for each word)
 * @param {string} str - Input string
 * @returns {string} Title cased string
 */
const toTitleCase = (str) => {
    if (!str || str === 'N/A') return str;
    return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

/**
 * Format timestamp to browser's local timezone with proper date/time display
 * Handles UTC timestamps and converts them to user's actual local timezone
 * (India timezone if user is in India, US timezone if user is in US, etc.)
 */
const formatLocalDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';

    try {
        // Get user's actual browser timezone
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        // If the timestamp looks like an ISO date but lacks 'Z', add it to force UTC interpretation
        let isoStr = timestamp;
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(timestamp)) {
            isoStr = timestamp + 'Z';
        }
        
        const date = new Date(isoStr);
        
        if (isNaN(date.getTime())) {
            console.error('Invalid date:', timestamp);
            return 'N/A';
        }

        // Format date and time separately for better display in user's local timezone
        const dateOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: userTimeZone
        };

        const timeOptions = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZone: userTimeZone
        };

        const formattedDate = date.toLocaleDateString('en-US', dateOptions);
        const formattedTime = date.toLocaleTimeString('en-US', timeOptions);

        return `${formattedDate} | ${formattedTime}`;
    } catch (error) {
        console.error('Error formatting date:', error);
        return timestamp;
    }
};

/**
 * Fax Agent Dashboard Main Component
 */
const FaxAgentDashboard = () => {
    // Restore active tab from sessionStorage or default to "inbox"
    const [activeTab, setActiveTab] = useState(() => {
        const savedTab = sessionStorage.getItem('faxDashboardActiveTab');
        return savedTab || "inbox";
    });
    const [showCreateTemplateDialog, setShowCreateTemplateDialog] = useState(false);
    const [showSendFaxDialog, setShowSendFaxDialog] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // API data state
    const [faxData, setFaxData] = useState([]);
    const [purchasedNumbers, setPurchasedNumbers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    
    // Tab counts - stored separately to persist across tab switches
    const [tabCounts, setTabCounts] = useState({
        inbox: 0,
        sent: 0,
        'my-numbers': 0
    });

    const { themeMode, currentTheme } = useTheme();
    const { selectedMenu, selectMenu } = useMenu();
    const navigate = useNavigate();

    /**
     * Fetch fax data based on current tab using service layer
     * Uses direction-based filtering: INBOUND (inbox), OUTBOUND (sent), DRAFT (drafts)
     */
    const fetchFaxData = async () => {
        try {
            setLoading(true);

            if (activeTab === 'my-numbers') {
                const numbersResponse = await FaxAgentService.getPurchasedNumbers();
                // getPurchasedNumbers returns the array directly, not wrapped in .data
                const numbers = Array.isArray(numbersResponse) ? numbersResponse : [];
                setPurchasedNumbers(numbers);
                setTotalCount(numbers.length);
                // Update tab count for my-numbers
                setTabCounts(prev => ({ ...prev, 'my-numbers': numbers.length }));
                setLoading(false);
                return;
            }

            // Parameters for service layer calls  
            const params = {
                limit: rowsPerPage,
                skip: page * rowsPerPage
            };

            // Service method mapping - eliminates repetitive switch case
            const serviceMethodMap = {
                'inbox': { method: FaxAgentService.getInbound, label: 'INBOUND' },
                'sent': { method: FaxAgentService.getOutbound, label: 'OUTBOUND' },
                'drafts': { method: FaxAgentService.getDrafts, label: 'DRAFT' },
                'templates': { method: FaxAgentService.getTemplates, label: 'TEMPLATES' }
            };

            const serviceConfig = serviceMethodMap[activeTab] || serviceMethodMap['inbox'];

            // Special handling for templates (no pagination)
            if (activeTab === 'templates') {
                const templateResponse = await serviceConfig.method();
                const templates = Array.isArray(templateResponse) ? templateResponse : [];
                setFaxData(templates);
                setTotalCount(templates.length);
                return;
            }

            // Regular fax data with pagination
            const response = await serviceConfig.method(params);

            // Handle service layer response format - ensure data is always an array
            const responseData = Array.isArray(response?.data) ? response.data : [];
            const responseTotalCount = response?.totalCount || responseData.length || 0;

            setFaxData(responseData);
            setTotalCount(responseTotalCount);
            
            // Update tab count for the current tab
            setTabCounts(prev => ({ ...prev, [activeTab]: responseTotalCount }));

        } catch (err) {
            console.error('❌ Error fetching fax data:', err);
            setFaxData([]);
            setTotalCount(0);
            message.error(err.message || 'Failed to fetch fax data');
        } finally {
            setLoading(false);
        }
    };
    
    /**
     * Fetch counts for all tabs on initial mount
     * Fetches with limit=50 to ensure totalCount is returned correctly by API
     */
    const fetchAllTabCounts = async () => {
        try {
            // Fetch counts for all tabs in parallel
            const [inboxResponse, sentResponse, numbersResponse] = await Promise.all([
                FaxAgentService.getInbound({ limit: 50, skip: 0 }),
                FaxAgentService.getOutbound({ limit: 50, skip: 0 }),
                FaxAgentService.getPurchasedNumbers()
            ]);
            
            setTabCounts({
                inbox: inboxResponse?.totalCount || inboxResponse?.data?.length || 0,
                sent: sentResponse?.totalCount || sentResponse?.data?.length || 0,
                'my-numbers': Array.isArray(numbersResponse) ? numbersResponse.length : 0
            });
        } catch (error) {
            console.error('❌ Error fetching tab counts:', error);
        }
    };

    // Preprocess faxes to flatten nested fields for search (using real API response structure)
    // Safety check: ensure faxData is an array before mapping
    const faxDataArray = Array.isArray(faxData) ? faxData : [];
    const flatFaxes = faxDataArray.map((f) => ({
        ...f,
        // Map real API fields - preserve email separately
        emailId: f.emailId || "",
        senderName: f.emailId || "",
        senderNumber: f.fromNumber || "",
        recipientName: f.emailId || "",
        recipientNumber: f.toNumber || "",
        // Patient name - preserved as-is from backend (case-sensitive, no transformation)
        patientName: f.patientName || "N/A",
        // Format timestamps in local timezone
        formattedDate: formatLocalDateTime(f.createdAt),
        formattedSentDate: formatLocalDateTime(f.sentAt),
        formattedReceivedDate: formatLocalDateTime(f.receivedAt),
        // File information from real API
        fileCount: f.fileCount || 0,
        totalPages: f.totalPages || 0,
        totalCost: f.totalCost || 0,
        // Status mapping
        displayStatus: f.status || f.direction || "Unknown"
    }));

    // Use smart search hook with current fax data (server-side filtering already applied)
    const { filteredItems, query, setQuery } = useSmartOSearch({
        items: flatFaxes,
        filterKeys: ["senderName", "recipientName", "patientName", "subject", "fromNumber", "toNumber"],
        searchQuery: "",
        sortKey: "createdAt",
        sortOrder: "desc",
    });

    const filteredFaxes = query ? filteredItems : flatFaxes;

    /**
     * Tab counts are now handled by totalCount from API response for active tab
     */

    /**
     * Handles tab change and resets pagination
     */
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPage(0); // Reset to first page when switching tabs
        setQuery(""); // Reset search query when switching tabs
        // Save active tab to sessionStorage for when user navigates back from detail page
        sessionStorage.setItem('faxDashboardActiveTab', tab);
    };

    /**
     * Pagination handlers - matching Home.js pattern
     */
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    /**
     * Handles creating a template
     */
    const handleCreateTemplate = async () => {
        try {
            message.success('Template created successfully');
            setShowCreateTemplateDialog(false);
        } catch (err) {
            message.error('Failed to create template. Please try again.');
        }
    };

    // Sync menu selection
    useEffect(() => {
        if (selectedMenu !== "Fax Agent") {
            selectMenu("Fax Agent");
        }
    }, [selectedMenu, selectMenu]);
    
    // Fetch counts for all tabs on initial mount
    useEffect(() => {
        fetchAllTabCounts();
    }, []);

    // Fetch data when activeTab, page, or rowsPerPage changes
    useEffect(() => {
        fetchFaxData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, page, rowsPerPage]); // Re-fetch when tab changes or pagination changes

    // Reset page when search query changes (for client-side filtering)
    useEffect(() => {
        if (query) {
            setPage(0);
        }
    }, [query]);

    // Persist active tab to sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem('faxDashboardActiveTab', activeTab);
    }, [activeTab]);


    /**
     * Tab configuration - counts are always visible using stored tabCounts
     */
    const tabs = [
        { key: 'inbox', label: 'Inbox', count: tabCounts.inbox },
        { key: 'sent', label: 'Sent', count: tabCounts.sent },
        { key: 'my-numbers', label: 'My Numbers', count: tabCounts['my-numbers'] },
        // { key: 'drafts', label: 'Drafts', count: tabCounts.drafts || 0 },
        // { key: 'templates', label: 'Templates', count: tabCounts.templates || 0 },
    ];

    // Dynamic table columns configuration based on active tab
    const getColumns = () => {
        // Fax Number column - changes based on tab: Inbox shows "From Number", Sent shows "To Number"
        const faxNumberColumn = activeTab === 'sent' ? {
            id: "toNumber",
            label: "Fax Number",
            minWidth: 160,
            width: 160,
            fixed: true,
            renderCell: (row) => (
                <div style={{ fontSize: '14px', color: currentTheme?.text?.primary || '#ffffff' }}>
                    {formatFaxNumber(row.toNumber)}
                </div>
            ),
        } : {
            id: "fromNumber",
            label: "Fax Number",
            minWidth: 160,
            width: 160,
            fixed: true,
            renderCell: (row) => (
                <div style={{ fontSize: '14px', color: currentTheme?.text?.primary || '#ffffff' }}>
                    {formatFaxNumber(row.fromNumber)}
                </div>
            ),
        };

        // Build columns array - Date & Time at the end
        const columns = [
            faxNumberColumn,
            // Email column - only show in Sent tab
            ...(activeTab === 'sent' ? [{
                id: "email",
                label: "Email",
                minWidth: 200,
                width: 200,
                renderCell: (row) => (
                    <div style={{ 
                        fontSize: '14px',
                        color: currentTheme?.text?.primary || '#ffffff',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {row.emailId || 'N/A'}
                    </div>
                ),
            }] : []),
            // Patient Name column - Title Case
            {
                id: "patient",
                label: "Patient Name",
                minWidth: 150,
                width: 150,
                renderCell: (row) => (
                    <div style={{ fontSize: '14px', color: currentTheme?.text?.primary || '#ffffff' }}>
                        {toTitleCase(row.patientName) || 'N/A'}
                    </div>
                ),
            },
            // Files column
            {
                id: "files",
                label: "Files",
                minWidth: 100,
                width: 100,
                renderCell: (row) => (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '2px 8px',
                            border: `1px solid ${currentTheme?.border?.primary || '#302d4c'}`,
                            borderRadius: '4px',
                            fontSize: '14px',
                            color: currentTheme?.text?.primary || '#ffffff'
                        }}>
                            <img 
                                src="/icons/pdf-icon.webp" 
                                alt="PDF" 
                                style={{ width: '12px', height: '15px' }} 
                            />
                            {row.fileCount || 0} {row.fileCount === 1 ? 'file' : 'files'}
                        </div>
                    </div>
                ),
            },
            // Date & Time column at the end
            {
                id: "dateTime",
                label: "Date & Time",
                minWidth: 140,
                width: 140,
                renderCell: (row) => {
                    // Parse the formatted date to split into date and time
                    const formattedParts = row.formattedDate?.split(' | ') || ['N/A', ''];
                    const date = formattedParts[0] || 'N/A';
                    const time = formattedParts[1] || '';
                    
                    return (
                        <div>
                            <div style={{ fontSize: '14px', color: currentTheme?.text?.primary || '#ffffff' }}>
                                {date}
                            </div>
                            {time && (
                                <div style={{ fontSize: '14px', color: currentTheme?.text?.primary || '#ffffff', opacity: 0.7 }}>
                                    {time}
                                </div>
                            )}
                        </div>
                    );
                },
            },
        ];

        return columns;
    };

    return (
        <CustomContainer containerWidth="90%">

            {/* Fixed Header Section */}
            <div style={{
                padding: '20px 20px 0 20px',
                overflowX: 'hidden',
                backgroundColor: currentTheme?.callhistory?.mainBackground || '#04002b',
                flexShrink: 0,
                flex: 'none',
                position: 'relative',
                zIndex: 10,
                width: '100%',
                boxSizing: 'border-box',
            }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '12px',
                    width: '100%',
                }}>
                    <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                        <h1 style={{
                            margin: 0,
                            fontSize: '24px',
                            color: currentTheme?.text?.primary || '#ffffff',
                            fontWeight: 'bold'
                        }}>
                            Fax Dashboard
                        </h1>
                        <p style={{
                            margin: '8px 0 16px 0',
                            color: currentTheme?.text?.secondary || '#9b99aa',
                            fontSize: '14px'
                        }}>
                            Manage your fax communications
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {/* Commented out for now - Create Template button
                        <button
                            onClick={() => navigate('/fax-agent/create-template')}
                            style={{
                                background: 'transparent',
                                border: `1px solid ${currentTheme?.border?.primary || '#302d4c'}`,
                                borderRadius: '4px',
                                padding: '6px 12px',
                                color: currentTheme?.text?.primary || '#ffffff',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '500',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <AddIcon style={{ fontSize: '14px' }} />
                            Create Template
                        </button>
                        */}
                        <button
                            onClick={() => navigate('/fax-agent/buy-fax-number')}
                            style={{
                                background: 'transparent',
                                border: '1px solid #FF6B47',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                color: currentTheme?.text?.primary || '#ffffff',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '500',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <AddIcon style={{ fontSize: '14px' }} />
                            Buy Fax Number
                        </button>
                        {/* <button
                            onClick={() => navigate('/fax-agent/add-provider')}
                            style={{
                                background: 'transparent',
                                border: '1px solid #4CAF50',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                color: currentTheme?.text?.primary || '#ffffff',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '500',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <AddIcon style={{ fontSize: '14px' }} />
                            Add Provider
                        </button> */}
                        <button
                            onClick={() => setShowSendFaxDialog(true)}
                            style={{
                                background: '#FF6B47',
                                border: '1px solid #FF6B47',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '500',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <SendIcon style={{ fontSize: '14px' }} />
                            Send Fax
                        </button>
                    </div>
                </div>

                {/* Full width horizontal line */}
                <div style={{
                    height: '1px',
                    backgroundColor: currentTheme?.border?.primary || '#302d4c',
                    // marginLeft: '-20px',
                    marginBottom: '16px',
                    // marginTop: '-16px'
                }} />

                {/* Tabs and Search/Filter Row */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px',
                    width: '100%',
                    boxSizing: 'border-box',
                }}>
                    {/* Tabs */}
                    <div style={{ 
                        display: 'flex', 
                        gap: '24px',
                        flexWrap: 'wrap',
                        minWidth: 0,
                        flex: '1 1 auto',
                    }}>
                        {tabs.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => handleTabChange(tab.key)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: '8px 0',
                                    cursor: 'pointer',
                                    color: activeTab === tab.key ? '#FF6B47' : currentTheme?.text?.secondary || '#9b99aa',
                                    borderBottom: activeTab === tab.key ? '2px solid #FF6B47' : '2px solid transparent',
                                    fontSize: '14px',
                                    fontWeight: activeTab === tab.key ? 'bold' : 'normal',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {tab.label}
                                {tab.count > 0 && (
                                    <span style={{
                                        backgroundColor: activeTab === tab.key ? '#FF6B47' : currentTheme?.border?.primary || '#302d4c',
                                        color: activeTab === tab.key ? 'white' : currentTheme?.text?.secondary || '#9b99aa',
                                        borderRadius: '5px',
                                        padding: '3px 6px',
                                        fontSize: '12px',
                                        minWidth: '20px',
                                        textAlign: 'center'
                                    }}>
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Search and Filter - Hide in My Numbers tab */}
                    {activeTab !== 'my-numbers' && (
                        <div style={{ 
                            display: 'flex', 
                            gap: '16px', 
                            alignItems: 'center',
                            flexShrink: 0,
                flex: 'none',
                        }}>
                            {/* Filter Dropdown */}
                            {/* <FilterDropdown
                                filterGroups={FAX_FILTER_GROUPS}
                                selectedFilters={selectedFilters}
                                onFiltersChange={handleFiltersChange}
                                placeholder="Filter"
                                sx={{ minWidth: '120px' }}
                            /> */}

                            {/* Search Input */}
                            <div style={{ 
                                width: '250px', 
                                maxWidth: '100%',
                                minWidth: '200px',
                                flexShrink: 0,
                flex: 'none',
                            }}>
                                <Search
                                    value={query}
                                    onChange={setQuery}
                                    themeMode={themeMode}
                                    currentTheme={currentTheme}
                                    style={{ width: "100%", padding: '20px 10px', borderRadius: '8px', boxSizing: 'border-box' }}
                                    placeholder="Search faxes..."
                                    fontSize=".9rem"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Table Section */}
            {loading ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    padding: '40px 20px',
                }}>
                    <img
                        style={{
                            width: "250px",
                            height: "250px"
                        }}
                        src={
                            themeMode === "dark"
                                ? "/icons/loader_white.gif"
                                : "/icons/loader_black.gif"
                        }
                        alt="Loading"
                    />
                </div>
            ) : activeTab === 'my-numbers' ? (
                <PurchasedNumbers numbers={purchasedNumbers} />
            ) : filteredFaxes && filteredFaxes.length > 0 ? (
                <div style={{
                    padding: '0 20px 20px 20px',
                    width: '100%',
                    boxSizing: 'border-box',
                }}>
                    <StickyFlexTable
                        columns={getColumns()}
                        rows={filteredFaxes}
                        loading={false}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        rowsCount={query ? filteredFaxes.length : totalCount}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        // selectionEnabled={true}
                        // selectedKeys={selectedFaxes}
                        // onSelectedKeysChange={setSelectedFaxes}
                        // checkboxColor={currentTheme?.border?.primary || '#302d4c'}
                        containerHeight="calc(100vh - 380px)"
                        paginationMargin={0}
                        rowSelector={(row) => {
                            // Save active tab before navigating so we can return to it
                            sessionStorage.setItem('faxDashboardActiveTab', activeTab);
                            navigate(`/fax-agent/received-fax/${row._id || row.faxId}`);
                        }}
                    />
                </div>
            ) : (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: themeMode === 'dark' ? '#ffffff' : '#000000',
                    fontSize: '16px',
                    padding: '40px 20px',
                }}>
                    No Records Found
                </div>
            )}



            {/* Create Template Dialog */}
            <CustomDialog
                open={showCreateTemplateDialog}
                onClose={() => setShowCreateTemplateDialog(false)}
                title="Create Template"
                themeMode={themeMode}
                currentTheme={currentTheme}
            >
                <div style={{ padding: '20px' }}>
                    <p>Template creation form would go here...</p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                        <Button
                            label="Cancel"
                            onClick={() => setShowCreateTemplateDialog(false)}
                            variant="outlined"
                        />
                        <Button
                            label="Create Template"
                            onClick={() => handleCreateTemplate({})}
                            style={{ backgroundColor: '#FF6B47', borderColor: '#FF6B47' }}
                        />
                    </div>
                </div>
            </CustomDialog>

            {/* Enhanced Send Fax Panel */}
            {showSendFaxDialog && (
                <EnhancedSendFaxPanel
                    currentTheme={currentTheme}
                    onClose={() => setShowSendFaxDialog(false)}
                    onSuccess={fetchFaxData}
                />
            )}
        </CustomContainer>
    );
};

export default FaxAgentDashboard;
